import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

/**
 * "Set / reset password" (docs/323-ADMIN-CREDENTIAL-MANAGEMENT.md v0 Fix A) as an MUI `Dialog` —
 * mirrors `apps/admin-web/src/components/SetPasswordModal.tsx` 1:1 (same 8-character floor, same
 * copy). Also reactivates a soft-deleted account and claims a docs/286-provisioned one —
 * unchanged server behavior, this is presentation only.
 */
export function SetPasswordDialog({
  open,
  userEmail,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  userEmail: string | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (password: string) => void;
}) {
  const [password, setPassword] = useState("");
  const tooShort = password.length > 0 && password.length < 8;

  const close = () => {
    setPassword("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>Set / reset password{userEmail ? ` — ${userEmail}` : ""}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Sets this account's password directly. Also reactivates a removed account and claims a
          pending (invite-free) one. Every existing session for this user is revoked.
        </Typography>
        <TextField
          type="password"
          label="New password (min. 8 characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
          error={tooShort}
          helperText={tooShort ? "Password must be at least 8 characters." : undefined}
          autoFocus
          fullWidth
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={busy || password.length < 8} onClick={() => onSubmit(password)}>
          Set password
        </Button>
      </DialogActions>
    </Dialog>
  );
}
